/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { BundleType } from '#resources/nls/bundle';
import type { Keys } from '#utils/UNSAFE_keys';
import type {
  CurrentItemVariant,
  ListItemRendererContext,
  SelectionDetail
} from '#UNSAFE_Collection';
import type { ItemPadding } from './List.types';

import { memo } from 'preact/compat';
import { useInteractionStyle } from '#hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { isSameKey } from '#utils/UNSAFE_keys';
import { Selector } from '#UNSAFE_Selector';
import { getListRowClassArray } from './utils/ListThemeUtils';
import { ListCell } from './ListCell';

/**
 * Props for the ListRow Component
 */
type ListRowProps<K extends string | number, D> = {
  listId: string;
  rowKey: K;
  rowData: D;
  rowIndex: number;
  isFinalRow: boolean;
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  padding: ItemPadding;
  hasTopGridline: boolean;
  hasBottomGridline: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isSelectionEnabled: boolean;
  isSingleSelectionEnabled: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  selectedRowKeys: Keys<K>;
  isShowFocusRing: boolean;
  isActive: boolean;
  isTabbableMode: boolean;
  isSuggestion: boolean;
  variant?: CurrentItemVariant;
  onSelectionChange?: (detail: SelectionDetail<K>, isExtendableSelection: boolean) => void;
  disabledInteractionKeys?: ('enter' | 'space')[];
};

/**
 * The internal component used to render a single row in List.
 */
export function ListRow<K extends string | number, D>({
  listId,
  rowKey,
  rowData,
  rowIndex,
  isFinalRow,
  children,
  padding,
  hasTopGridline,
  hasBottomGridline,
  isSelected,
  isPreviousSelected,
  isSelectionEnabled,
  isSingleSelectionEnabled,
  selectedRowKeys,
  isShowFocusRing,
  isActive,
  isTabbableMode,
  isSuggestion,
  variant,
  disabledInteractionKeys,
  onSelectionChange,
  isRtl,
  isHighContrast
}: ListRowProps<K, D>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle({ disabledKeys: disabledInteractionKeys });

  const rowClasses = classNames(
    getListRowClassArray({
      isFinalRowIndex: isFinalRow,
      hasTopGridline,
      hasBottomGridline,
      isSelected,
      isPreviousSelected,
      isShowFocusRing,
      isSelectionEnabled,
      isSingleSelection: isSingleSelectionEnabled,
      isActive: applyActiveStyle,
      isHover: applyHoverStyle,
      isPseudoHover: applyPseudoHoverStyle,
      isHighContrast,
      variant,
      padding
    })
  );

  const accessibleRowName = (rowIndex + 2).toString();
  const handleRowSelectorChange = (detail: SelectionDetail<K>) => {
    if (onSelectionChange != null) {
      onSelectionChange(
        {
          value: detail.value,
          target: detail.target
        },
        false
      );
    }
  };
  const selectorRenderer =
    isSelectionEnabled && !isSingleSelectionEnabled
      ? () => {
          // conversion from 0-based index and adding the header row as it is always rendered
          return (
            <Selector
              aria-label={translations.collection_selectRow({ ROW_NAME: accessibleRowName })}
              onChange={handleRowSelectorChange}
              rowKey={rowKey}
              selectedKeys={selectedRowKeys}
            />
          );
        }
      : undefined;

  const hasTabIndex = isActive && !isTabbableMode;

  const cellProps = {
    listId,
    rowData,
    rowKey,
    rowIndex,
    isSelectionEnabled,
    isSelected,
    hasTabIndex,
    isTabbableMode,
    renderer: children,
    selectorRenderer,
    isRtl,
    isHighContrast
  };
  return (
    <div
      aria-rowindex={rowIndex + 1}
      class={rowClasses}
      role={'row'}
      key={rowKey}
      data-oj-key={rowKey}
      data-oj-list-row={listId}
      {...(typeof rowKey === 'number' && { 'data-oj-key-type': 'number' })}
      {...(isSuggestion && { 'data-oj-suggestion': true })}
      {...(isSelectionEnabled && interactionProps)}>
      <ListCell {...cellProps} />
    </div>
  );
}

export const MemoizeListRow = memo(
  ListRow,
  (prev: ListRowProps<string | number, unknown>, next: ListRowProps<string | number, unknown>) => {
    if (prev && next) {
      return (
        prev.listId === next.listId &&
        prev.rowKey === next.rowKey &&
        prev.rowData === next.rowData &&
        prev.rowIndex === next.rowIndex &&
        prev.isFinalRow === next.isFinalRow &&
        prev.children === next.children &&
        prev.padding === next.padding &&
        prev.hasTopGridline === next.hasTopGridline &&
        prev.hasBottomGridline === next.hasBottomGridline &&
        prev.isSelected === next.isSelected &&
        prev.isPreviousSelected === next.isPreviousSelected &&
        prev.isSelectionEnabled === next.isSelectionEnabled &&
        prev.isSingleSelectionEnabled === next.isSingleSelectionEnabled &&
        (prev.isSelectionEnabled && !prev.isSingleSelectionEnabled
          ? isSameKey(prev.selectedRowKeys, next.selectedRowKeys)
          : true) &&
        prev.isShowFocusRing === next.isShowFocusRing &&
        prev.isActive === next.isActive &&
        prev.isTabbableMode === next.isTabbableMode &&
        prev.isSuggestion === next.isSuggestion &&
        prev.variant === next.variant &&
        prev.onSelectionChange === next.onSelectionChange &&
        prev.isRtl === next.isRtl &&
        prev.isHighContrast === next.isHighContrast &&
        prev.disabledInteractionKeys === next.disabledInteractionKeys
      );
    }
    return false;
  }
);
